import { useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.clawyee.com/api';

export function useDeployments() {
  const { getAccessToken } = useAuth();

  const getDeployments = useCallback(async (filters) => {
    const token = getAccessToken();
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.limit) params.set('limit', String(filters.limit));

    const queryString = params.toString();
    const url = `${API_BASE}/deployments${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch deployments');
    }
    return data.deployments || [];
  }, [getAccessToken]);

  const deleteDeployment = useCallback(async (deploymentId) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/deployments/${deploymentId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete deployment');
    }
    return true;
  }, [getAccessToken]);

  const stopDeployment = useCallback(async (deploymentId) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/deployments/${deploymentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ action: 'stop' }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to stop deployment');
    }
    return true;
  }, [getAccessToken]);

  const updateDeploymentConfig = useCallback(async (deploymentId, updates) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/deployments/${deploymentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ config: updates }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update deployment config');
    }
    return {
      deployment: data.deployment,
      impact: data.impact
    };
  }, [getAccessToken]);

  const retryRun = useCallback(async (instanceId) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/agent-run`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ instanceId }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to retry run');
    }
    return data;
  }, [getAccessToken]);

  return {
    getDeployments,
    deleteDeployment,
    stopDeployment,
    retryRun,
    updateDeploymentConfig,
  };
}
